<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';
import ContactMetrics from 'dashboard/components-next/ContactMetrics/ContactMetrics.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteContactDialogRef = ref(null);
const avatarFile = ref(null);
const avatarUrl = ref('');
const contactsFormRef = ref(null);
const showMetricsModal = ref(false);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);
const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() =>
  contactData.value?.createdAt ? dynamicTime(contactData.value.createdAt) : ''
);

const lastActivityAt = computed(() =>
  contactData.value?.lastActivityAt ? dynamicTime(contactData.value.lastActivityAt) : ''
);

const avatarSrc = computed(() =>
  avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail
);

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    await store.dispatch('contacts/fetchContactableInbox', props.selectedContact.id);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;
  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact?.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-6 pb-6">

    <!-- Avatar + name + meta + labels -->
    <div class="flex flex-col items-start gap-3 px-0 w-full">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="72"
        allow-upload
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1 w-full">
        <!-- pe-16 no mobile evita sobreposição com o sidebar-toggle fixo (w-16 z-50) do ContactsDetailsLayout -->
        <div class="flex items-center gap-2 pe-16 lg:pe-0">
          <h3 class="text-base font-medium text-n-slate-12 flex-1 min-w-0 truncate">
            {{ selectedContact?.name }}
          </h3>
          <button
            v-if="selectedContact?.id"
            class="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-n-slate-10 hover:text-n-teal-11 hover:bg-n-teal-3 transition-colors"
            :title="$t('CONTACT_METRICS.MODAL.TITLE')"
            @click="showMetricsModal = true"
          >
            <span class="i-lucide-bar-chart-2 size-5" />
          </button>
        </div>
        <div class="flex flex-col gap-1.5">
          <span
            v-if="selectedContact?.identifier"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ selectedContact?.identifier }}
          </span>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span
              v-if="selectedContact?.identifier"
              class="i-ph-activity text-n-slate-10 size-4"
            />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{ $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', { date: lastActivityAt }) }}
          </span>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
    </div>

    <!-- Contact form -->
    <div class="flex flex-col items-start gap-6 w-full">
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        @update="handleFormUpdate"
      />
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>

    <Policy :permissions="['administrator']">
      <div class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong">
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>

  </div>

  <!-- Metrics full-screen modal -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showMetricsModal"
        class="fixed inset-0 z-[9999] flex flex-col bg-n-solid-1"
        @keydown.esc="showMetricsModal = false"
      >
        <!-- Modal header -->
        <div class="flex items-center gap-3 px-4 sm:px-6 h-14 border-b border-n-weak flex-shrink-0">
          <div class="flex items-center justify-center w-7 h-7 rounded-lg bg-n-teal-3">
            <span class="i-lucide-bar-chart-2 size-4 text-n-teal-11" />
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ selectedContact?.name }}
            </p>
            <p class="text-xs text-n-slate-10 leading-none mt-0.5">
              {{ $t('CONTACT_METRICS.MODAL.SUBTITLE') }}
            </p>
          </div>
          <button
            class="flex items-center justify-center w-10 h-10 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-solid-3 transition-colors flex-shrink-0"
            @click="showMetricsModal = false"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <!-- Modal body — scrollable -->
        <div class="flex-1 overflow-y-auto">
          <ContactMetrics
            v-if="selectedContact?.id"
            :contact-id="selectedContact.id"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
